/**
 * BLRRD Editor — Storage permission + quota helpers.
 *
 * `navigator.storage.persist()` requests the browser keep our origin's data
 * across cache eviction (Chrome/Edge auto-grants for installed PWAs and
 * frequently-used origins; Safari requires a user gesture). We ask once per
 * session — repeated calls cost nothing but dirty the audit log.
 *
 * `estimate()` reports usage vs quota; the UI can surface this when we add
 * project-level Save flows in 1.E.
 *
 * Both APIs may be missing (private mode, older browsers). Helpers always
 * resolve — never throw — so call sites stay simple.
 */

let persistRequested = false;
let persistGranted   = null;

/* Idempotent. Returns true if storage is persisted (or was already), false
 * if denied, null if the API isn't available. Safe to call from any user
 * gesture path (snapshot save, look save). */
export async function requestPersist() {
    if (typeof navigator === 'undefined' || !navigator.storage || typeof navigator.storage.persist !== 'function') {
        return null;
    }
    /* If we already asked, don't ask again — re-issuing won't change state. */
    if (persistRequested) return persistGranted;
    persistRequested = true;
    try {
        /* `persisted()` is cheaper and tells us if we already have it. */
        if (typeof navigator.storage.persisted === 'function') {
            const already = await navigator.storage.persisted();
            if (already) { persistGranted = true; return true; }
        }
        const granted = await navigator.storage.persist();
        persistGranted = !!granted;
        return persistGranted;
    } catch {
        persistGranted = null;
        return null;
    }
}

/* Returns { usageMB, quotaMB, percent } or null if unavailable. */
export async function getStorageEstimate() {
    if (typeof navigator === 'undefined' || !navigator.storage || typeof navigator.storage.estimate !== 'function') {
        return null;
    }
    try {
        const est = await navigator.storage.estimate();
        const usage = est.usage || 0;
        const quota = est.quota || 0;
        return {
            usageMB: Math.round(usage / (1024 * 1024) * 10) / 10,
            quotaMB: Math.round(quota / (1024 * 1024)),
            percent: quota > 0 ? Math.round((usage / quota) * 1000) / 10 : 0,
        };
    } catch {
        return null;
    }
}
